import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFileSrc } from "@/hooks/useFileSrc";
import {
  Send, MessageCircle, Mic, MicOff, Paperclip, X, Volume2, CheckCheck, Download,
} from "lucide-react";
import { toast } from "sonner";

const CANNED = [
  "Running about 15 mins late",
  "At pickup, waiting for loading",
  "On the way to delivery",
  "Delivered, collecting signature",
  "Traffic delay, ETA +30 mins",
  "Unable to deliver, no one home",
  "Load secured, departing now",
];

export default function ChatPanel({ jobId, jobRef }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const [showCanned, setShowCanned] = useState(false);
  const [attachPreview, setAttachPreview] = useState(null);

  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const bottomRef = useRef(null);
  const recorderRef = useRef(null);
  const recTimerRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/jobs/${jobId}/chat`);
      setMessages(data);
    } catch {}
  }, [jobId]);

  useEffect(() => {
    loadMessages();
    const token = localStorage.getItem("df_token");
    const wsBase = API.replace(/^http/, "ws");
    const ws = new WebSocket(`${wsBase}/ws/chat/${jobId}?token=${token || ""}`);
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        setMessages((prev) => (prev.find((m) => m.id === msg.id) ? prev : [...prev, msg]));
      } catch {}
    };
    ws.onerror = () => {
      setConnected(false);
      pollRef.current = setInterval(loadMessages, 4000);
    };
    ws.onclose = () => setConnected(false);
    return () => {
      ws.close();
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [jobId, loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(trimmed);
        setText("");
      } else {
        const { data } = await api.post(`/jobs/${jobId}/chat`, { text: trimmed });
        setText("");
        setMessages((prev) => (prev.find((m) => m.id === data.id) ? prev : [...prev, data]));
      }
    } catch {
      toast.error("Failed to send");
    } finally {
      setSending(false);
    }
  };

  const sendAttachment = async (file, type) => {
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("attachment_type", type);
      if (text.trim()) fd.append("text", text.trim());
      const { data } = await api.post(`/jobs/${jobId}/chat/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMessages((prev) => (prev.find((m) => m.id === data.id) ? prev : [...prev, data]));
      setText("");
      setAttachPreview(null);
    } catch {
      toast.error("Failed to send attachment");
    } finally {
      setSending(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: "audio/webm" });
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: "audio/webm" });
        await sendAttachment(file, "audio");
        setRecSecs(0);
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
      let s = 0;
      recTimerRef.current = setInterval(() => {
        s += 1;
        setRecSecs(s);
        if (s >= 120) stopRecording();
      }, 1000);
    } catch {
      toast.error("Microphone access denied");
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    clearInterval(recTimerRef.current);
    setRecording(false);
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachPreview({ file, url: URL.createObjectURL(file) });
    e.target.value = "";
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="flex flex-col" style={{ minHeight: 0 }}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <MessageCircle size={14} className="text-[#19e6c8]" />
        <span className="text-xs font-semibold text-[#19e6c8] uppercase tracking-widest">
          Chat · {jobRef}
        </span>
        <span
          className={`ml-auto w-2 h-2 rounded-full ${connected ? "bg-green-400" : "bg-white/20"}`}
          title={connected ? "Live" : "Polling"}
        />
      </div>

      {/* Messages */}
      <div
        className="flex-1 overflow-y-auto space-y-2 pr-1"
        style={{ maxHeight: "clamp(180px, 35vh, 320px)", minHeight: "80px" }}
      >
        {messages.length === 0 && (
          <p className="text-xs text-white/30 text-center py-4">No messages yet.</p>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
              <span className="text-[10px] text-white/35 mb-0.5 px-1">
                {m.sender_name} · {new Date(m.created_at).toLocaleTimeString("en-AU", { timeStyle: "short" })}
              </span>
              <div
                className={`px-3 py-2 text-sm max-w-[85%] break-words ${
                  mine ? "chat-bubble-mine" : "chat-bubble-theirs"
                }`}
              >
                {m.attachment_type === "audio" ? (
                  <AudioMsg path={m.attachment_url} />
                ) : m.attachment_type === "image" ? (
                  <ImageMsg path={m.attachment_url} caption={m.text} />
                ) : (
                  m.text
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Attachment preview */}
      {attachPreview && (
        <div className="flex items-center gap-2 mt-2 p-2 rounded-xl bg-white/5 border border-white/10">
          <img src={attachPreview.url} alt="" className="h-10 w-10 rounded-lg object-cover" />
          <span className="flex-1 text-xs text-white/50 truncate">{attachPreview.file.name}</span>
          <button onClick={() => setAttachPreview(null)} className="text-white/40 hover:text-white">
            <X size={14} />
          </button>
          <button
            onClick={() => sendAttachment(attachPreview.file, "image")}
            disabled={sending}
            className="px-3 py-1 rounded-lg text-xs text-white font-semibold"
            style={{ background: "linear-gradient(90deg,#2b5fff,#3b82f6)" }}
          >
            Send
          </button>
        </div>
      )}

      {/* Canned messages */}
      {showCanned && (
        <div className="mt-2 rounded-xl bg-black/30 border border-white/10 max-h-36 overflow-y-auto">
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/10">
            <span className="text-[10px] text-white/40 uppercase tracking-widest">Quick Messages</span>
            <button onClick={() => setShowCanned(false)} className="text-white/30 hover:text-white">
              <X size={12} />
            </button>
          </div>
          {CANNED.map((c) => (
            <button
              key={c}
              className="w-full text-left px-3 py-2 text-xs text-white/70 hover:text-white hover:bg-white/[0.04] border-b border-white/5 last:border-0"
              onClick={() => { setText(c); setShowCanned(false); }}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {/* Input row */}
      <div className="flex items-center gap-1.5 mt-3">
        {/* Canned msgs */}
        <button
          onClick={() => setShowCanned((s) => !s)}
          className={`p-1.5 rounded-lg transition-colors ${
            showCanned ? "text-[#19e6c8] bg-[#19e6c8]/10" : "text-white/35 hover:text-white/60"
          }`}
          title="Quick messages"
        >
          <CheckCheck size={15} />
        </button>

        {/* Attach */}
        <button
          onClick={() => fileInputRef.current?.click()}
          className="p-1.5 rounded-lg text-white/35 hover:text-white/60 transition-colors"
          title="Attach image"
        >
          <Paperclip size={15} />
        </button>
        <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onFileChange} />

        {/* Text */}
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          placeholder="Type a message…"
          className="flex-1 bg-white/5 border border-white/15 rounded-xl px-3 py-2 text-sm text-white placeholder-white/25 focus:outline-none focus:border-[#19e6c8]"
          data-testid="chat-input"
        />

        {/* Audio / Send */}
        {text.trim() || attachPreview ? (
          <button
            onClick={send}
            disabled={sending || (!text.trim() && !attachPreview)}
            className="p-2 rounded-xl text-white disabled:opacity-40"
            style={{ background: "linear-gradient(90deg,#2b5fff,#3b82f6)" }}
            data-testid="chat-send"
          >
            <Send size={14} />
          </button>
        ) : recording ? (
          <button
            onClick={stopRecording}
            className="p-2 rounded-xl text-white recording-btn"
            style={{ background: "#ef4444" }}
            title={`Stop (${recSecs}s)`}
          >
            <MicOff size={14} />
          </button>
        ) : (
          <button
            onClick={startRecording}
            className="p-2 rounded-xl text-white/40 hover:text-white/70 hover:bg-white/10 transition-colors"
            title="Record voice message"
          >
            <Mic size={14} />
          </button>
        )}
      </div>

      {recording && (
        <div className="flex items-center gap-2 mt-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          <span className="text-[10px] text-red-400 font-semibold">Recording {recSecs}s — tap mic to stop</span>
        </div>
      )}
    </div>
  );
}

// ── Authenticated attachment renderers ────────────────────────────────────────
function AudioMsg({ path }) {
  const src = useFileSrc(path);
  const filename = path?.split("/").pop() || "audio.webm";
  return (
    <div className="flex items-center gap-2">
      <Volume2 size={13} className="text-amber-400 shrink-0" />
      {src ? (
        <>
          <audio src={src} controls className="h-7 max-w-[140px]" />
          <a href={src} download={filename} className="text-white/40 hover:text-white/80 transition-colors" title="Download">
            <Download size={13} />
          </a>
        </>
      ) : (
        <span className="text-xs text-white/40">Loading audio…</span>
      )}
    </div>
  );
}

function ImageMsg({ path, caption }) {
  const src = useFileSrc(path);
  const filename = path?.split("/").pop() || "image";
  return (
    <div>
      {src ? (
        <div className="relative inline-block">
          <a href={src} target="_blank" rel="noopener noreferrer">
            <img src={src} alt="attachment" className="max-w-[150px] rounded-lg mb-1 cursor-pointer hover:opacity-90 transition-opacity" />
          </a>
          <a
            href={src}
            download={filename}
            className="absolute top-1 right-1 p-0.5 rounded bg-black/50 text-white hover:bg-black/80 transition-colors"
            title="Download"
          >
            <Download size={11} />
          </a>
        </div>
      ) : (
        <div className="w-32 h-24 rounded-lg bg-white/10 animate-pulse" />
      )}
      {caption && <p className="text-sm mt-1">{caption}</p>}
    </div>
  );
}
